// Filling in a form.
//
// The picker comes FIRST: which form, and what it is about. Starting a draft
// pins the template version, so it cannot happen until both are chosen — and
// doing it any other way would mean creating a draft the person then abandons
// because they picked the wrong machine.
//
// OFFLINE (round 42, docs/offline-forms-plan.md). The form being filled in
// lives ON THE PHONE (src/offline/), and the sync engine sends it. With signal
// that is the autosave this page always had, landing a second or so after
// typing stops; with none, it waits, and the bar says so. The picker reads the
// phone's saved copy of the forms and their pick-lists, refreshed whenever
// there is signal, so a form can be started, filled, photographed, signed and
// submitted in a dead spot, and it sends the next time Dayboard is open with
// signal. Start still asks the server FIRST when it can, so the refusals it
// always gave (a machine the form is not for, a closed job) arrive before the
// first question rather than after the last.
//
// Answers are written to the phone on every change, so a phone backgrounded
// mid-form (the normal way one gets left) has lost nothing. The keepalive
// save on `visibilitychange` stays as well: with signal, it gets the last
// words to the server before the tab is frozen.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  formFill,
  type FormContent,
  type PackForm,
  type Submission,
  type SubmissionFile,
  type TemplateAsset,
} from '../formsApi';
import { MachineCard, machineFromSnapshot } from '../components/forms/MachineCard';
import { ApiError } from '../api';
import { useSession } from '../session';
import { FormSections } from '../components/forms/FormSections';
import type { FormFileSource } from '../components/forms/fileSource';
import { EmptyState, PageHeader, useConfirm } from '../components/ui';
import { askToPersist, offlineStore } from '../offline/store';
import { newId, ownerKey, isSynced, type LocalForm } from '../offline/localForm';
import { useFormsPack } from '../offline/pack';
import { forgetForm, onFormChange } from '../offline/sync';
import { sendNow, sendState, SEND_STATE_LABEL } from '../offline/runner';
import { formatMomentAu } from '../lib/dates';

const AUTOSAVE_MS = 1200;

/**
 * "Answered", the way the countdown and the refused-submit markers mean it.
 * `setAnswer` deletes empties, but answers can also arrive from the server
 * row, so the empty shapes are all named.
 */
function isEmptyAnswer(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function isUnreachable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504)
  );
}

/** A server draft, taken onto the phone so it can be finished with no signal. */
function recordFromServer(input: {
  owner: string;
  submission: Submission;
  content: FormContent;
  meters: LocalForm['meters'];
  templateName: string;
  aboutLabel: string | null;
  machine: LocalForm['machine'];
  options: LocalForm['options'];
}): LocalForm {
  const now = new Date().toISOString();
  const { submission } = input;
  return {
    id: submission.id,
    owner: input.owner,
    templateId: submission.templateId,
    templateName: input.templateName,
    versionId: submission.templateVersionId,
    linkKind: submission.kind === 'asset' ? 'asset' : submission.jobId ? 'job' : 'any',
    jobId: submission.jobId,
    resourceId: submission.resourceId,
    aboutLabel: input.aboutLabel,
    machine: input.machine,
    content: input.content,
    meters: input.meters,
    options: input.options,
    answers: submission.answers,
    files: submission.files.map((f) => ({
      id: f.id,
      fieldKey: f.fieldKey,
      kind: f.kind,
      filename: f.kind === 'signature' ? 'signature.png' : 'photo.jpg',
      uploaded: true,
    })),
    removedFileIds: [],
    created: true,
    startedOnPhone: false,
    revision: 0,
    savedRevision: 0,
    submitAt: null,
    problem: null,
    problems: [],
    createdAt: submission.createdAt,
    updatedAt: now,
  };
}

/** The dropdowns behind a form's "Pick from the app" questions, live. */
async function fetchRefOptions(
  submissionId: string,
  content: FormContent
): Promise<LocalForm['options']> {
  const options: LocalForm['options'] = {};
  for (const field of content.sections.flatMap((s) => s.fields)) {
    if (field.type !== 'entity_ref') continue;
    try {
      options[field.key] = (await formFill.refOptions(submissionId, field.key)).options;
    } catch {
      // That dropdown stays on "Loading…"; the rest of the form works.
    }
  }
  return options;
}

export function FormFillPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, tenant } = useSession();
  const owner = user && tenant ? ownerKey(tenant.id, user.id) : null;
  // Round 28: the landing links straight to /forms/fill/:templateId, so the
  // form is already chosen and the picker only asks what it is about. The
  // route WITHOUT the param stays mounted — it owns `?draft=`, the
  // continuation URL every unfinished form already carries.
  const { templateId: routeTemplateId } = useParams<{ templateId?: string }>();
  const [params, setParams] = useSearchParams();
  const draftId = params.get('draft');

  const packState = useFormsPack(owner);
  const pack = packState.pack;

  // The form on this phone, while it is being filled in.
  const [form, setForm] = useState<LocalForm | null>(null);
  // A form Dayboard has as SUBMITTED: shown from the server, read-only.
  const [record, setRecord] = useState<{
    submission: Submission;
    content: FormContent;
    meters: LocalForm['meters'];
    jobTitle: string | null;
  } | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [staleVersion, setStaleVersion] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  // Answers typed but not yet handed to the sync engine (the debounce).
  const [pendingSave, setPendingSave] = useState(false);
  // The refused-submit markers: the required fields that were empty when
  // Submit was pressed. Each marker renders only while its field is STILL
  // empty, so answering a question clears its own marker (the list a person
  // is working through must describe the form as it is).
  const [marks, setMarks] = useState<ReadonlySet<string>>(new Set());
  // Object URLs for photos still on the phone, so they draw with no signal.
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});

  // The picker's state, before a form exists.
  const [templateId, setTemplateId] = useState(routeTemplateId ?? '');
  const [targetId, setTargetId] = useState('');

  useEffect(() => {
    if (routeTemplateId) setTemplateId(routeTemplateId);
  }, [routeTemplateId]);

  const machinesById = useMemo(
    () => new Map((pack?.machines ?? []).map((m) => [m.id, m])),
    [pack]
  );
  const chosen: PackForm | undefined = pack?.forms.find((t) => t.id === templateId);
  // The machines THIS form is offered against (round 28), server-filtered by
  // the same predicate the start route enforces.
  const templateAssets: TemplateAsset[] | null =
    chosen?.linkKind === 'asset'
      ? chosen.machineIds.map((id) => machinesById.get(id)).filter((m): m is TemplateAsset => !!m)
      : null;
  const assetsFiltered = !!chosen?.content.asset_filter;

  // --- Opening a form ---------------------------------------------------

  const loadForm = useCallback(
    async (id: string) => {
      setError(null);
      const local = await offlineStore()
        .getForm(id)
        .catch(() => undefined);
      if (local && local.owner === owner) {
        setForm(local);
        setAnswers(local.answers);
        setRecord(null);
        setProblems(local.problems);
        return;
      }
      if (local) {
        // ANOTHER account on this phone holds this form, possibly with
        // answers not yet sent. Taking it over would write into their copy,
        // so the page says so instead.
        setError(
          'Another account on this phone is filling in this form and has not sent it yet. Sign in as them to finish it.'
        );
        return;
      }
      if (!owner) return;
      try {
        const result = await formFill.get(id);
        if (result.submission.status !== 'draft') {
          setRecord({
            submission: result.submission,
            content: result.content,
            meters: result.meters,
            jobTitle: result.jobTitle,
          });
          setForm(null);
          return;
        }
        // A draft Dayboard has and this phone does not (started on another
        // device, or before this phone kept forms): take it onto the phone,
        // so from here on it is filled in exactly like one started here.
        const next = recordFromServer({
          owner,
          submission: result.submission,
          content: result.content,
          meters: result.meters,
          templateName: result.content.title,
          aboutLabel: result.submission.jobNumber
            ? result.jobTitle
              ? `${result.submission.jobNumber} · ${result.jobTitle}`
              : result.submission.jobNumber
            : null,
          machine: machineFromSnapshot(result.submission.resourceSnapshot),
          options: await fetchRefOptions(result.submission.id, result.content),
        });
        await offlineStore()
          .putForm(next)
          .catch(() => undefined);
        setForm(next);
        setAnswers(next.answers);
        setStaleVersion(result.staleVersion);
        setRecord(null);
      } catch (err) {
        setError(
          isUnreachable(err)
            ? 'This form is not on this phone, and Dayboard cannot be reached right now.'
            : err instanceof ApiError
              ? err.message
              : 'Could not open that form.'
        );
      }
    },
    [owner]
  );

  useEffect(() => {
    if (draftId && owner) void loadForm(draftId);
  }, [draftId, owner, loadForm]);

  // "The office has updated this form since you started", for a form started
  // on this phone: the saved pack names the current version.
  useEffect(() => {
    if (!form || !pack) return;
    const current = pack.forms.find((f) => f.id === form.templateId);
    if (current && current.versionId !== form.versionId) setStaleVersion(true);
  }, [form, pack]);

  // Keep the record's meta current as the sync engine records steps (created,
  // uploaded, saved, refused). The ANSWERS on screen are not replaced: they
  // are what the person is typing, and the store already has them.
  useEffect(() => {
    if (!form) return;
    const id = form.id;
    return onFormChange((changed) => {
      if (changed !== id) return;
      void offlineStore()
        .getForm(id)
        .then((latest) => {
          if (!latest) return;
          setForm(latest);
          if (latest.problems.length > 0) setProblems(latest.problems);
        })
        .catch(() => undefined);
    });
  }, [form?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Photos still on the phone get object URLs; uploaded ones read the server.
  useEffect(() => {
    if (!form || typeof URL.createObjectURL !== 'function') return;
    let cancelled = false;
    const made: string[] = [];
    void (async () => {
      const urls: Record<string, string> = {};
      for (const file of form.files) {
        const blob = await offlineStore()
          .getBlob(file.id)
          .catch(() => undefined);
        if (blob) {
          urls[file.id] = URL.createObjectURL(blob);
          made.push(urls[file.id]!);
        }
      }
      if (!cancelled) setBlobUrls(urls);
    })();
    return () => {
      cancelled = true;
      for (const url of made) URL.revokeObjectURL(url);
    };
    // Re-run when the set of files changes, not on every keystroke.
  }, [form?.id, form?.files.map((f) => f.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Saving -------------------------------------------------------------

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const send = useCallback(async (id: string) => {
    setSaving(true);
    try {
      return await sendNow(id);
    } finally {
      setSaving(false);
    }
  }, []);

  const queueSend = useCallback(
    (id: string) => {
      setPendingSave(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setPendingSave(false);
        void send(id);
      }, AUTOSAVE_MS);
    },
    [send]
  );

  useEffect(() => {
    // A phone backgrounding the browser is the NORMAL way a form gets left.
    // The answers are already on the phone; with signal, this gets the last of
    // them to the server before the tab is frozen. `keepalive` lets the
    // request outlive the page, which is why it is a bare fetch. The next
    // sync resends regardless, so nothing is marked as saved on its account.
    function onHidden() {
      if (document.visibilityState !== 'hidden' || !form?.created) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      setPendingSave(false);
      void fetch(`/api/forms/submissions/${form.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ answers: answersRef.current }),
      }).catch(() => undefined);
    }
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [form?.id, form?.created]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const answersRef = useRef(answers);
  answersRef.current = answers;

  function setAnswer(key: string, value: unknown) {
    if (!form) return;
    const empty =
      value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    setAnswers((prev) => {
      const next = { ...prev };
      if (empty) delete next[key];
      else next[key] = value;
      return next;
    });
    // One key at a time against the STORED answers, so this cannot undo a
    // change the sync engine made to another key in the meantime.
    const id = form.id;
    void offlineStore()
      .updateForm(id, (f) => {
        const answersNow = { ...f.answers };
        if (empty) delete answersNow[key];
        else answersNow[key] = value;
        return {
          ...f,
          answers: answersNow,
          revision: f.revision + 1,
          problem: null,
          updatedAt: new Date().toISOString(),
        };
      })
      .then(() => queueSend(id))
      .catch(() => undefined);
  }

  // --- Photos and the signature, on the phone first ---------------------

  const fileSource: FormFileSource | undefined = form
    ? {
        url: (fileId) => blobUrls[fileId] ?? formFill.fileUrl(fileId),
        upload: async (fieldKey, kind, blob, filename) => {
          const id = newId();
          const store = offlineStore();
          await store.putBlob(id, blob);
          const updated = await store.updateForm(form.id, (f) => ({
            ...f,
            files: [...f.files, { id, fieldKey, kind, filename, uploaded: false }],
            updatedAt: new Date().toISOString(),
          }));
          if (updated) setForm(updated);
          if (typeof URL.createObjectURL === 'function') {
            setBlobUrls((urls) => ({ ...urls, [id]: URL.createObjectURL(blob) }));
          }
          askToPersist();
          return { id };
        },
        remove: async (fileId) => {
          const store = offlineStore();
          const updated = await store.updateForm(form.id, (f) => {
            const file = f.files.find((x) => x.id === fileId);
            return {
              ...f,
              files: f.files.filter((x) => x.id !== fileId),
              // Uploaded already: the server copy comes off on the next sync.
              removedFileIds: file?.uploaded ? [...f.removedFileIds, fileId] : f.removedFileIds,
              updatedAt: new Date().toISOString(),
            };
          });
          await store.deleteBlob(fileId).catch(() => undefined);
          if (updated) setForm(updated);
        },
      }
    : undefined;

  // --- The required countdown and the refused-submit markers -----------

  const content = form?.content ?? record?.content ?? null;

  /** Required questions still unanswered, in the order they are asked. */
  const requiredMissing = useMemo(() => {
    if (!content) return [] as string[];
    return content.sections
      .flatMap((s) => s.fields)
      .filter((f) => f.required && isEmptyAnswer(answers[f.key]))
      .map((f) => f.key);
  }, [content, answers]);

  /** The markers FormSections renders: only fields that were missing at the
      refused submit AND are still missing now. */
  const fieldProblems = useMemo(() => {
    const map = new Map<string, string>();
    if (marks.size > 0) {
      for (const key of requiredMissing) {
        if (marks.has(key)) map.set(key, 'Required.');
      }
    }
    return map;
  }, [marks, requiredMissing]);

  const scrollToFirstMissing = useCallback(() => {
    const first = requiredMissing[0];
    if (!first) return;
    // Optional-chained: jsdom has no scrollIntoView, and the markers matter
    // more than the scroll.
    document.getElementById(`q-${first}`)?.scrollIntoView?.({ behavior: 'auto', block: 'center' });
  }, [requiredMissing]);

  // Marks are only ever SET by a refused submit, so their arrival is the
  // moment to put the first missing question on screen. `behavior: 'auto'`,
  // the reduced-motion lesson the builder already carries.
  useEffect(() => {
    if (marks.size > 0) scrollToFirstMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marks]);

  // --- Starting and submitting ----------------------------------------

  // A "fill this in" link from a job or machine page carries a PRESET, NOT A
  // DECISION: if the preset is among what the form offers it is filled in; if
  // not, the picker stays and asks. A link must never be able to skip the
  // question the picker exists to ask (round 28, ZT's rule kept).
  const presetTarget = params.get(chosen?.linkKind === 'asset' ? 'resourceId' : 'jobId');
  useEffect(() => {
    if (!pack || !chosen || !presetTarget || draftId) return;
    const offered =
      chosen.linkKind === 'asset'
        ? (templateAssets ?? []).some((a) => a.id === presetTarget)
        : pack.jobs.some((j) => j.id === presetTarget);
    if (offered) setTargetId(presetTarget);
    // Not offered: leave the picker empty rather than guessing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack, chosen?.id]);

  async function discardDraft() {
    if (!form) return;
    const sure = await confirm({
      title: 'Delete this form?',
      message: 'Everything typed and every photo on it goes too. This cannot be undone.',
      confirmLabel: 'Delete this form',
      tone: 'danger',
    });
    if (!sure) return;
    setBusy(true);
    try {
      // A form that never reached Dayboard lives only here.
      if (form.created) {
        try {
          await formFill.discard(form.id);
        } catch (err) {
          // Already gone is what was asked for.
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
      await forgetForm(offlineStore(), form);
      navigate('/app/forms');
    } catch (err) {
      setError(
        isUnreachable(err)
          ? 'Dayboard already has this form, so deleting it needs signal. Try again when you have some.'
          : err instanceof ApiError
            ? err.message
            : 'Could not delete that form.'
      );
      setBusy(false);
    }
  }

  async function start() {
    if (!chosen || !owner) return;
    setBusy(true);
    setError(null);
    const id = newId();
    const target = chosen.linkKind === 'asset' ? { resourceId: targetId } : { jobId: targetId || null };
    const job = pack?.jobs.find((j) => j.id === targetId);
    const machine = chosen.linkKind === 'asset' ? (machinesById.get(targetId) ?? null) : null;
    const aboutLabel = job ? `${job.number} · ${job.title}` : (machine?.label ?? null);
    const base = {
      id,
      owner,
      templateId: chosen.id,
      templateName: chosen.name,
      linkKind: chosen.linkKind,
      jobId: chosen.linkKind === 'asset' ? null : targetId || null,
      resourceId: chosen.linkKind === 'asset' ? targetId : null,
      aboutLabel,
      machine,
      answers: {},
      files: [],
      removedFileIds: [],
      revision: 0,
      savedRevision: 0,
      submitAt: null,
      problem: null,
      problems: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    let next: LocalForm;
    try {
      // With signal, the server starts it and its refusals arrive NOW.
      const result = await formFill.start({ templateId: chosen.id, id, ...target });
      next = {
        ...base,
        versionId: result.submission.templateVersionId,
        content: result.content,
        meters: result.meters,
        options:
          result.submission.templateVersionId === chosen.versionId
            ? chosen.options
            : await fetchRefOptions(id, result.content),
        created: true,
        startedOnPhone: false,
      };
    } catch (err) {
      if (!isUnreachable(err)) {
        setError(err instanceof ApiError ? err.message : 'Could not start that form.');
        setBusy(false);
        return;
      }
      // No signal: start it on the phone, from the saved copy. The server
      // hears about it when there is signal, pinned to THIS version.
      next = {
        ...base,
        versionId: chosen.versionId,
        content: chosen.content,
        meters: chosen.linkKind === 'asset' ? (pack?.meters[targetId] ?? []) : [],
        options: chosen.options,
        created: false,
        startedOnPhone: true,
      };
    }
    try {
      await offlineStore().putForm(next);
    } catch {
      // A phone that cannot store: the form still works while this page is
      // open; it simply cannot survive a reload with no signal.
    }
    askToPersist();
    setForm(next);
    setAnswers({});
    setRecord(null);
    setBusy(false);
    // The id goes in the URL, so a reload or a share reopens the same
    // half-finished form rather than starting a new one.
    setParams({ draft: id });
  }

  async function submit() {
    if (!form) return;
    setError(null);
    // The required check happens HERE first, because with no signal there is
    // no server to ask. The server still decides (it validates the row, and
    // knows shapes this cannot), and its refusal comes back the same way.
    if (requiredMissing.length > 0) {
      const byKey = new Map(form.content.sections.flatMap((s) => s.fields).map((f) => [f.key, f]));
      setProblems(requiredMissing.map((key) => `${byKey.get(key)?.label || 'This question'} is required.`));
      setMarks(new Set(requiredMissing));
      return;
    }
    setBusy(true);
    setProblems([]);
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPendingSave(false);
    const pressedAt = new Date().toISOString();
    await offlineStore()
      .updateForm(form.id, (f) => ({
        ...f,
        submitAt: pressedAt,
        problem: null,
        problems: [],
        updatedAt: pressedAt,
      }))
      .catch(() => undefined);
    askToPersist();
    const outcome = await send(form.id);
    if (outcome.kind === 'sent') {
      navigate(`/app/forms/submissions/${form.id}`);
      return;
    }
    if (outcome.kind === 'refused') {
      const latest = await offlineStore()
        .getForm(form.id)
        .catch(() => undefined);
      if (latest) {
        setForm(latest);
        setProblems(latest.problems);
        if (latest.problem) setError(latest.problem);
        setMarks(new Set(requiredMissing));
      }
      setBusy(false);
      return;
    }
    // No signal, or signed out: it is finished and safe on the phone.
    navigate('/app/forms', { state: { queued: form.id } });
  }

  // --- The picker -----------------------------------------------------

  if (!draftId) {
    if (!pack) {
      return packState.error ? (
        <>
          <PageHeader title="Fill in a form" />
          <div className="banner-error" role="alert">
            {packState.error}
          </div>
        </>
      ) : (
        <p className="muted">Loading…</p>
      );
    }

    if (pack.forms.length === 0) {
      return (
        <>
          <PageHeader title="Fill in a form" />
          <EmptyState
            title="Nothing to fill in yet"
            body="Your office has not published any forms. Once they do, they will show up here."
          />
        </>
      );
    }

    // Linked here for a form that has since been archived or unpublished.
    // The draft already in somebody's hands still finishes (the mid-fill
    // rule); only STARTING a new one is refused, and this is where that
    // refusal reads politely.
    if (routeTemplateId && !chosen) {
      return (
        <>
          <PageHeader title="Fill in a form" />
          <EmptyState
            title="That form is no longer available"
            body="It may have been archived or taken down. The forms you can fill in are on the Forms page."
            action={
              <Link to="/app/forms" className="btn-primary btn-sm">
                All forms
              </Link>
            }
          />
        </>
      );
    }

    const targets =
      chosen?.linkKind === 'asset'
        ? (templateAssets ?? []).map((a) => ({ id: a.id, label: a.label }))
        : pack.jobs.map((j) => ({ id: j.id, label: `${j.number} · ${j.title}` }));
    const pickedMachine =
      chosen?.linkKind === 'asset'
        ? ((templateAssets ?? []).find((a) => a.id === targetId) ?? null)
        : null;

    return (
      <>
        <PageHeader
          title={routeTemplateId && chosen ? chosen.name : 'Fill in a form'}
          subtitle={
            routeTemplateId && chosen
              ? (chosen.description ?? 'Pick what it is about, then answer.')
              : 'Pick the form, then what it is about.'
          }
        />

        {error && (
          <div className="banner-error mb-4" role="alert">
            {error}
          </div>
        )}

        {packState.fromPhone && packState.savedAt && (
          <p className="hint mb-4 max-w-lg">
            No signal right now, so these lists are this phone's copy from{' '}
            {formatMomentAu(packState.savedAt)}. What you fill in is kept here and sends when
            there is signal.
          </p>
        )}

        <div className="max-w-lg space-y-4">
          {/* The form select only exists on the legacy route: the landing
              links straight to /forms/fill/:templateId with the form already
              chosen, and asking again would be a second answer to a settled
              question. */}
          {!routeTemplateId && (
            <div>
              <label className="form-label" htmlFor="pick-form">
                Form
              </label>
              <select
                id="pick-form"
                className="form-select"
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  // The target list changes with the form, so a stale pick from
                  // the previous one must not survive.
                  setTargetId('');
                }}
              >
                <option value="">Choose…</option>
                {pack.forms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {chosen?.description && <p className="hint">{chosen.description}</p>}
            </div>
          )}

          {chosen && chosen.linkKind !== 'any' && (
            <div>
              <label className="form-label" htmlFor="pick-target">
                {chosen.linkKind === 'asset' ? 'Machine' : 'Job'}
              </label>
              <select
                id="pick-target"
                className="form-select"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
              >
                <option value="">Choose…</option>
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              {targets.length === 0 && (
                <p className="hint">
                  {chosen.linkKind !== 'asset'
                    ? 'You are not on any open jobs.'
                    : assetsFiltered
                      ? 'No machines match this form.'
                      : 'No machines are set up yet.'}
                </p>
              )}
              {/* The mis-tap catch: the moment to notice you tapped EX-04
                  instead of EX-05 is before the questions, not after. */}
              {pickedMachine && (
                <div className="mt-2">
                  <MachineCard machine={pickedMachine} />
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy || !chosen || (chosen.linkKind !== 'any' && targetId === '')}
            onClick={() => void start()}
          >
            {busy ? 'Starting…' : 'Start'}
          </button>
        </div>
      </>
    );
  }

  // --- A submitted form, from the server --------------------------------

  if (record) {
    const { submission } = record;
    return (
      <>
        <PageHeader
          title={record.content.title || 'Form'}
          subtitle={
            submission.jobNumber
              ? record.jobTitle
                ? `${submission.jobNumber} · ${record.jobTitle}`
                : submission.jobNumber
              : submission.resourceId
                ? 'Machine prestart'
                : undefined
          }
        />
        <p className="mb-4 text-sm">
          <Link to="/app/forms" className="table-link">
            &larr; All forms
          </Link>
        </p>
        {submission.resourceSnapshot && (
          <div className="mb-4 max-w-2xl">
            <MachineCard machine={machineFromSnapshot(submission.resourceSnapshot)} />
          </div>
        )}
        <div className="max-w-2xl">
          <FormSections
            content={record.content}
            answers={submission.answers}
            files={submission.files}
            meters={record.meters}
            submissionId={submission.id}
            readOnly
          />
        </div>
      </>
    );
  }

  // --- The form itself -------------------------------------------------

  if (!form) {
    return error ? (
      <div className="banner-error" role="alert">
        {error}
      </div>
    ) : (
      <p className="muted">Loading…</p>
    );
  }

  // Submit was pressed on this phone and it has not gone yet: finished, and
  // shown as the record it will be, with where it is up to.
  const waitingToSend = form.submitAt !== null;
  const files: SubmissionFile[] = form.files.map((f) => ({
    id: f.id,
    fieldKey: f.fieldKey,
    kind: f.kind,
  }));
  const state = sendState(form);
  const saveLabel = saving
    ? 'Saving…'
    : pendingSave
      ? 'Waiting to save…'
      : isSynced(form)
        ? 'Saved as you go'
        : state === 'signed_out'
          ? 'Saved on this phone. Sign in to send.'
          : state === 'no_signal'
            ? 'Saved on this phone. Waiting for signal.'
            : 'Waiting to save…';

  return (
    <>
      <PageHeader
        title={form.content.title || form.templateName || 'Form'}
        subtitle={form.aboutLabel ?? (form.resourceId ? 'Machine prestart' : undefined)}
      />

      <p className="mb-4 text-sm">
        <Link to="/app/forms" className="table-link">
          &larr; All forms
        </Link>
      </p>

      {/* The machine as the record will state it. */}
      {form.machine && (
        <div className="mb-4 max-w-2xl">
          <MachineCard machine={form.machine} />
        </div>
      )}

      {waitingToSend && (
        <div className="banner-info mb-4 max-w-2xl" role="status">
          <strong>Submitted on this phone at {formatMomentAu(form.submitAt!)}.</strong>{' '}
          {state === 'signed_out'
            ? 'Sign in to Dayboard and it will send.'
            : 'It sends by itself the next time Dayboard is open with signal.'}
        </div>
      )}

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {form.problem && !error && (
        <div className="banner-error mb-4" role="alert">
          <strong>Dayboard could not take this form.</strong> {form.problem}
        </div>
      )}

      {problems.length > 0 && (
        <div className="banner-error mb-4" role="alert">
          <strong>Not finished yet</strong>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* The form was republished after this one was started. The questions
          on screen are the ones it will be judged against, and saying so is
          better than silently serving old questions. */}
      {staleVersion && !waitingToSend && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The office has updated this form since you started. Finish this one; it will be
          recorded against the questions you can see. Your next one will use the new version.
        </div>
      )}

      <div className="max-w-2xl">
        <FormSections
          content={form.content}
          answers={answers}
          files={files}
          meters={form.meters}
          refOptions={form.options}
          submissionId={form.id}
          readOnly={waitingToSend}
          problems={fieldProblems}
          onChange={(key, value) => setAnswer(key, value)}
          onFilesChanged={() => undefined}
          fileSource={fileSource}
        />
      </div>

      {!waitingToSend && (
        // A quiet exit, deliberately NOWHERE NEAR the Submit button: it sits
        // above the sticky bar with the form content, so a stray tap while
        // reaching for Submit cannot land on it. The confirm dialog is the
        // second gate. Round 28 — the endpoint shipped with round 16 and
        // nothing on screen ever called it.
        <p className="mb-4 max-w-2xl text-sm">
          <button
            type="button"
            className="table-link text-red-700"
            disabled={busy}
            onClick={() => void discardDraft()}
          >
            Delete this form
          </button>
        </p>
      )}

      {!waitingToSend && (
        // Sticky, above the phone tab bar. Somebody at the bottom of a
        // twenty-question prestart should not have to scroll back up to
        // finish, and the save state is what tells them it is safe to walk
        // away.
        <div className="sticky bottom-16 z-20 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur lg:bottom-0">
          <div className="flex items-center gap-3">
            {/* A live region: "walked away trusting the tick" is the whole
                promise, so the states are announced, not just drawn. With no
                signal the answers are still safe, ON THE PHONE, and the bar
                says exactly that rather than an error. */}
            <span className="text-sm text-gray-600" role="status">
              {saveLabel}
            </span>
            {requiredMissing.length > 0 &&
              (marks.size > 0 ? (
                // After a refused submit the countdown turns into the way
                // back to the first gap.
                <button
                  type="button"
                  className="row-action-danger"
                  onClick={scrollToFirstMissing}
                >
                  {requiredMissing.length} required to go
                </button>
              ) : (
                <span className="text-sm text-gray-600">
                  {requiredMissing.length} required to go
                </span>
              ))}
            <span className="spacer" />
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {waitingToSend && (
        <p className="text-sm text-gray-600">{SEND_STATE_LABEL[state]}</p>
      )}
    </>
  );
}
